import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, Upload, Download, Trash2, CheckCircle, XCircle, LogOut, Link as LinkIcon, Users, UserPlus, Check, X, MessageSquare, Send, FileText, Receipt, Edit, ChevronDown, ChevronUp, FolderOpen, Bell, BellOff, Pencil, Loader2, RefreshCw, Clock, FileBarChart, Search, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImplementationProjectDetail = ({ user, onLogout }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [generalDocuments, setGeneralDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Edit project dialog
  const [editProjectDialogOpen, setEditProjectDialogOpen] = useState(false);
  const [editProjectName, setEditProjectName] = useState('');
  const [editClientName, setEditClientName] = useState('');
  const [editDeadline, setEditDeadline] = useState('');
  const [editTotalBudget, setEditTotalBudget] = useState('');
  const [editTotalBudgetVat, setEditTotalBudgetVat] = useState('');
  const [editTemplateId, setEditTemplateId] = useState('');
  const [availableTemplates, setAvailableTemplates] = useState([]);
  
  // Financial report
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [financialReport, setFinancialReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  
  // Document note dialog
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editingNoteDoc, setEditingNoteDoc] = useState(null);
  const [noteText, setNoteText] = useState('');
  
  // Invoice dialog
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [newInvoice, setNewInvoice] = useState({ invoice_number: '', expense_category: '', net_amount: '', vat_amount: '' });
  const [invoiceSearch, setInvoiceSearch] = useState('');
  
  // Edit invoice dialog
  const [editInvoiceDialogOpen, setEditInvoiceDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  
  // Custom document dialog
  const [customDocDialogOpen, setCustomDocDialogOpen] = useState(false);
  const [selectedInvoiceForDoc, setSelectedInvoiceForDoc] = useState(null);
  const [customDocName, setCustomDocName] = useState('');
  
  // Edit document dialog
  const [editDocDialogOpen, setEditDocDialogOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [editDocName, setEditDocName] = useState('');
  
  // General document dialog
  const [generalDocDialogOpen, setGeneralDocDialogOpen] = useState(false);
  const [newGeneralDoc, setNewGeneralDoc] = useState({ document_name: '', category: 'general' });
  
  // Supplier dialog
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ name: '', email: '', phone: '' });
  
  // Review dialog
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewingDocument, setReviewingDocument] = useState(null);
  const [reviewData, setReviewData] = useState({ status: 'approved', comment: '' });
  const [reviewType, setReviewType] = useState('invoice'); // 'invoice' or 'general'
  
  // Assignment dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningDocument, setAssigningDocument] = useState(null);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [assignType, setAssignType] = useState('invoice');
  
  // Uploading state
  const [uploadingDocId, setUploadingDocId] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  
  // Edit supplier state
  const [editSupplierDialogOpen, setEditSupplierDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  
  // R2 Relink state
  const [relinkLoading, setRelinkLoading] = useState(false);
  const [relinkDialogOpen, setRelinkDialogOpen] = useState(false);
  const [relinkResult, setRelinkResult] = useState(null);
  const [relinkStep, setRelinkStep] = useState('preview'); // 'preview' | 'applied'
  
  // Collapse states
  const [generalDocsExpanded, setGeneralDocsExpanded] = useState(true);
  const [invoicesExpanded, setInvoicesExpanded] = useState(true);
  
  // Notification settings dialog
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    notification_enabled: true,
    notification_interval_days: 3,
    advisor_escalation_enabled: false,
    advisor_escalation_threshold: 3
  });
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Expiry tracking states
  const [expiryDialogOpen, setExpiryDialogOpen] = useState(false);
  const [expiryEditingDoc, setExpiryEditingDoc] = useState(null);
  const [expiryIssueDate, setExpiryIssueDate] = useState('');
  const [expiryExpiryDate, setExpiryExpiryDate] = useState('');
  const [expirySaving, setExpirySaving] = useState(false);

  useEffect(() => {
    fetchProjectDetails();
    fetchSuppliers();
    fetchExpenseCategories();
    fetchGeneralDocuments();
  }, [projectId]);

  // Auto-refresh every 30 seconds to catch collaborator uploads
  useEffect(() => {
    const interval = setInterval(() => {
      fetchProjectDetails();
      fetchGeneralDocuments();
      setLastRefresh(new Date());
    }, 30000);
    return () => clearInterval(interval);
  }, [projectId]);

  const handleManualRefresh = () => {
    fetchProjectDetails();
    fetchSuppliers();
    fetchGeneralDocuments();
    setLastRefresh(new Date());
    toast.success('Ανανεώθηκε');
  };

  const fetchProjectDetails = async () => {
    try {
      const [projectRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/implementation/projects/${projectId}`),
        axios.get(`${API}/implementation/projects/${projectId}/invoices`),
      ]);
      setProject(projectRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης έργου');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/implementation/projects/${projectId}/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchExpenseCategories = async () => {
    try {
      // Get project-specific expense categories (from template or global)
      const response = await axios.get(`${API}/implementation/projects/${projectId}/expense-categories`);
      setExpenseCategories(response.data);
    } catch (error) {
      console.error('Error fetching expense categories:', error);
    }
  };

  const fetchGeneralDocuments = async () => {
    try {
      const response = await axios.get(`${API}/implementation/projects/${projectId}/general-documents`);
      setGeneralDocuments(response.data);
    } catch (error) {
      console.error('Error fetching general documents:', error);
    }
  };

  const handleInitGeneralDocuments = async () => {
    try {
      const response = await axios.post(`${API}/implementation/projects/${projectId}/general-documents/init`);
      toast.success(response.data.message);
      fetchGeneralDocuments();
    } catch (error) {
      toast.error('Σφάλμα δημιουργίας γενικών δικαιολογητικών');
    }
  };

  const handleAddInvoice = async (e) => {
    e.preventDefault();
    if (!newInvoice.expense_category) {
      toast.error('Επιλέξτε κατηγορία δαπάνης');
      return;
    }
    
    const netAmount = parseFloat(newInvoice.net_amount) || 0;
    
    // Check limits before adding
    if (netAmount > 0 && project?.total_budget) {
      try {
        const reportRes = await axios.get(`${API}/implementation/projects/${projectId}/financial-report`);
        const report = reportRes.data;
        const currentTotalNet = report.actual?.total_net || 0;
        const budgetNet = report.budget?.total_net || 0;
        
        let warnings = [];
        
        // Check category limit
        const selectedCategory = expenseCategories.find(c => c.key === newInvoice.expense_category);
        if (selectedCategory?.max_percentage > 0) {
          const categoryData = report.categories?.find(c => c.category_key === newInvoice.expense_category);
          const currentCatTotal = categoryData?.total_net || 0;
          const limitAmount = (selectedCategory.max_percentage / 100) * budgetNet;
          const newCatTotal = currentCatTotal + netAmount;
          
          if (newCatTotal > limitAmount) {
            const overAmount = (newCatTotal - limitAmount).toLocaleString('el-GR', { minimumFractionDigits: 2 });
            warnings.push(
              `⚠️ ΥΠΕΡΒΑΣΗ ΟΡΙΟΥ ΚΑΤΗΓΟΡΙΑΣ "${selectedCategory.name}":\n` +
              `   Όριο: €${limitAmount.toLocaleString('el-GR', { minimumFractionDigits: 2 })} (${selectedCategory.max_percentage}%)\n` +
              `   Τρέχον: €${currentCatTotal.toLocaleString('el-GR', { minimumFractionDigits: 2 })}\n` +
              `   Νέο ποσό: €${netAmount.toLocaleString('el-GR', { minimumFractionDigits: 2 })}\n` +
              `   Υπέρβαση: €${overAmount}`
            );
          }
        }
        
        // Check total budget limit
        const newTotalNet = currentTotalNet + netAmount;
        if (newTotalNet > budgetNet) {
          const overBudget = (newTotalNet - budgetNet).toLocaleString('el-GR', { minimumFractionDigits: 2 });
          warnings.push(
            `⚠️ ΥΠΕΡΒΑΣΗ ΣΥΝΟΛΙΚΟΥ ΠΡΟΫΠΟΛΟΓΙΣΜΟΥ:\n` +
            `   Προϋπολογισμός: €${budgetNet.toLocaleString('el-GR', { minimumFractionDigits: 2 })}\n` +
            `   Τρέχουσα απορρόφηση: €${currentTotalNet.toLocaleString('el-GR', { minimumFractionDigits: 2 })}\n` +
            `   Νέο ποσό: €${netAmount.toLocaleString('el-GR', { minimumFractionDigits: 2 })}\n` +
            `   Υπέρβαση: €${overBudget}`
          );
        }
        
        // Show warnings if any
        if (warnings.length > 0) {
          const confirmed = window.confirm(
            warnings.join('\n\n') + '\n\n' +
            'Θέλετε να συνεχίσετε;'
          );
          if (!confirmed) return;
        }
      } catch (err) {
        console.error('Error checking limits:', err);
      }
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/implementation/projects/${projectId}/invoices`, {
        ...newInvoice,
        net_amount: newInvoice.net_amount ? parseFloat(newInvoice.net_amount) : null,
        vat_amount: newInvoice.vat_amount ? parseFloat(newInvoice.vat_amount) : null
      });
      toast.success('Το τιμολόγιο προστέθηκε με τα δικαιολογητικά της κατηγορίας');
      setNewInvoice({ invoice_number: '', expense_category: '', net_amount: '', vat_amount: '' });
      setInvoiceDialogOpen(false);
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα προσθήκης τιμολογίου');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    try {
      await axios.delete(`${API}/implementation/invoices/${invoiceId}`);
      toast.success('Το τιμολόγιο διαγράφηκε');
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής');
    }
  };

  const handleEditInvoice = async (e) => {
    e.preventDefault();
    if (!editingInvoice) return;
    
    const netAmount = parseFloat(editingInvoice.net_amount) || 0;
    
    // Find original invoice to calculate difference
    const originalInvoice = invoices.find(inv => inv.invoice_id === editingInvoice.invoice_id);
    const originalNetAmount = originalInvoice?.net_amount || 0;
    const netDifference = netAmount - originalNetAmount;
    
    // Check limits before updating (only if amount increased)
    if (netDifference > 0 && project?.total_budget) {
      try {
        const reportRes = await axios.get(`${API}/implementation/projects/${projectId}/financial-report`);
        const report = reportRes.data;
        const currentTotalNet = report.actual?.total_net || 0;
        const budgetNet = report.budget?.total_net || 0;
        
        let warnings = [];
        
        // Check category limit
        const selectedCategory = expenseCategories.find(c => c.key === editingInvoice.expense_category);
        if (selectedCategory?.max_percentage > 0) {
          const categoryData = report.categories?.find(c => c.category_key === editingInvoice.expense_category);
          const currentCatTotal = categoryData?.total_net || 0;
          const limitAmount = (selectedCategory.max_percentage / 100) * budgetNet;
          const newCatTotal = currentCatTotal + netDifference;
          
          if (newCatTotal > limitAmount) {
            const overAmount = (newCatTotal - limitAmount).toLocaleString('el-GR', { minimumFractionDigits: 2 });
            warnings.push(
              `⚠️ ΥΠΕΡΒΑΣΗ ΟΡΙΟΥ ΚΑΤΗΓΟΡΙΑΣ "${selectedCategory.name}":\n` +
              `   Όριο: €${limitAmount.toLocaleString('el-GR', { minimumFractionDigits: 2 })} (${selectedCategory.max_percentage}%)\n` +
              `   Τρέχον: €${(currentCatTotal - originalNetAmount).toLocaleString('el-GR', { minimumFractionDigits: 2 })}\n` +
              `   Νέο ποσό: €${netAmount.toLocaleString('el-GR', { minimumFractionDigits: 2 })}\n` +
              `   Υπέρβαση: €${overAmount}`
            );
          }
        }
        
        // Check total budget limit
        const newTotalNet = currentTotalNet + netDifference;
        if (newTotalNet > budgetNet) {
          const overBudget = (newTotalNet - budgetNet).toLocaleString('el-GR', { minimumFractionDigits: 2 });
          warnings.push(
            `⚠️ ΥΠΕΡΒΑΣΗ ΣΥΝΟΛΙΚΟΥ ΠΡΟΫΠΟΛΟΓΙΣΜΟΥ:\n` +
            `   Προϋπολογισμός: €${budgetNet.toLocaleString('el-GR', { minimumFractionDigits: 2 })}\n` +
            `   Τρέχουσα απορρόφηση: €${(currentTotalNet - originalNetAmount).toLocaleString('el-GR', { minimumFractionDigits: 2 })}\n` +
            `   Νέο ποσό: €${netAmount.toLocaleString('el-GR', { minimumFractionDigits: 2 })}\n` +
            `   Υπέρβαση: €${overBudget}`
          );
        }
        
        // Show warnings if any
        if (warnings.length > 0) {
          const confirmed = window.confirm(
            warnings.join('\n\n') + '\n\n' +
            'Θέλετε να συνεχίσετε;'
          );
          if (!confirmed) return;
        }
      } catch (err) {
        console.error('Error checking limits:', err);
      }
    }
    
    setLoading(true);
    try {
      await axios.put(`${API}/implementation/invoices/${editingInvoice.invoice_id}`, {
        invoice_number: editingInvoice.invoice_number,
        expense_category: editingInvoice.expense_category,
        net_amount: editingInvoice.net_amount ? parseFloat(editingInvoice.net_amount) : null,
        vat_amount: editingInvoice.vat_amount ? parseFloat(editingInvoice.vat_amount) : null
      });
      toast.success('Το τιμολόγιο ενημερώθηκε');
      setEditInvoiceDialogOpen(false);
      setEditingInvoice(null);
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης τιμολογίου');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomDocument = async (e) => {
    e.preventDefault();
    if (!customDocName.trim() || !selectedInvoiceForDoc) return;
    setLoading(true);
    try {
      await axios.post(`${API}/implementation/invoices/${selectedInvoiceForDoc.invoice_id}/documents`, {
        document_name: customDocName,
      });
      toast.success('Το δικαιολογητικό προστέθηκε');
      setCustomDocName('');
      setCustomDocDialogOpen(false);
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα προσθήκης δικαιολογητικού');
    } finally {
      setLoading(false);
    }
  };

  const handleEditDocument = async (e) => {
    e.preventDefault();
    if (!editDocName.trim() || !editingDocument) return;
    try {
      await axios.put(`${API}/implementation/documents/${editingDocument.document_id}?document_name=${encodeURIComponent(editDocName)}`);
      toast.success('Το δικαιολογητικό ενημερώθηκε');
      setEditDocDialogOpen(false);
      setEditingDocument(null);
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης');
    }
  };

  const handleEditGeneralDocument = async (e) => {
    e.preventDefault();
    if (!editDocName.trim() || !editingDocument) return;
    try {
      await axios.put(`${API}/implementation/general-documents/${editingDocument.document_id}?document_name=${encodeURIComponent(editDocName)}`);
      toast.success('Το δικαιολογητικό ενημερώθηκε');
      setEditDocDialogOpen(false);
      setEditingDocument(null);
      fetchGeneralDocuments();
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      await axios.delete(`${API}/implementation/documents/${documentId}`);
      toast.success('Το δικαιολογητικό διαγράφηκε');
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής');
    }
  };

  const handleDeleteGeneralDocument = async (documentId) => {
    try {
      await axios.delete(`${API}/implementation/general-documents/${documentId}`);
      toast.success('Το δικαιολογητικό διαγράφηκε');
      fetchGeneralDocuments();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής');
    }
  };

  const handleAddGeneralDocument = async (e) => {
    e.preventDefault();
    if (!newGeneralDoc.document_name.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/implementation/projects/${projectId}/general-documents`, newGeneralDoc);
      toast.success('Το δικαιολογητικό προστέθηκε');
      setNewGeneralDoc({ document_name: '', category: 'general' });
      setGeneralDocDialogOpen(false);
      fetchGeneralDocuments();
    } catch (error) {
      toast.error('Σφάλμα προσθήκης');
    } finally {
      setLoading(false);
    }
  };

  // Edit project name and client
  const handleEditProject = async () => {
    try {
      await axios.put(`${API}/implementation/projects/${projectId}`, {
        project_title: editProjectName,
        client_name: editClientName,
        deadline: editDeadline || null,
        total_budget: editTotalBudget ? parseFloat(editTotalBudget) : null,
        total_budget_vat: editTotalBudgetVat ? parseFloat(editTotalBudgetVat) : null,
        template_id: editTemplateId || null
      });
      toast.success('Τα στοιχεία του έργου ενημερώθηκαν');
      setEditProjectDialogOpen(false);
      fetchProjectDetails();
      fetchGeneralDocuments();
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης έργου');
    }
  };

  const openEditProjectDialog = () => {
    setEditProjectName(project?.project_title || project?.project_name || '');
    setEditClientName(project?.client_name || '');
    setEditDeadline(project?.deadline || '');
    setEditTotalBudget(project?.total_budget?.toString() || '');
    setEditTotalBudgetVat(project?.total_budget_vat?.toString() || '');
    setEditTemplateId(project?.template_id || '');
    fetchImplTemplates();
    setEditProjectDialogOpen(true);
  };

  const fetchFinancialReport = async () => {
    setLoadingReport(true);
    try {
      const response = await axios.get(`${API}/implementation/projects/${projectId}/financial-report`);
      setFinancialReport(response.data);
      setReportDialogOpen(true);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης αναφοράς');
    } finally {
      setLoadingReport(false);
    }
  };

  // Document note functions
  const openNoteDialog = (doc, isGeneral = false) => {
    setEditingNoteDoc({ ...doc, isGeneral });
    setNoteText(doc.note || '');
    setNoteDialogOpen(true);
  };

  const handleSaveNote = async () => {
    try {
      const endpoint = editingNoteDoc.isGeneral
        ? `${API}/implementation/general-documents/${editingNoteDoc.document_id}/note`
        : `${API}/implementation/documents/${editingNoteDoc.document_id}/note`;
      await axios.put(endpoint, {
        note: noteText || null
      });
      toast.success('Η σημείωση αποθηκεύτηκε');
      setNoteDialogOpen(false);
      if (editingNoteDoc.isGeneral) {
        fetchGeneralDocuments();
      } else {
        fetchProjectDetails();
      }
    } catch (error) {
      toast.error('Σφάλμα αποθήκευσης σημείωσης');
    }
  };

  const handleDeleteNote = async () => {
    try {
      const endpoint = editingNoteDoc.isGeneral
        ? `${API}/implementation/general-documents/${editingNoteDoc.document_id}/note`
        : `${API}/implementation/documents/${editingNoteDoc.document_id}/note`;
      await axios.delete(endpoint);
      toast.success('Η σημείωση διαγράφηκε');
      setNoteDialogOpen(false);
      if (editingNoteDoc.isGeneral) {
        fetchGeneralDocuments();
      } else {
        fetchProjectDetails();
      }
    } catch (error) {
      toast.error('Σφάλμα διαγραφής σημείωσης');
    }
  };

  const handleUpload = async (documentId, files, isGeneral = false) => {
    if (!files || files.length === 0) return;
    setUploadingDocId(documentId);
    const formData = new FormData();
    // Append all files
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }
    try {
      const endpoint = isGeneral 
        ? `${API}/implementation/general-documents/${documentId}/upload`
        : `${API}/implementation/documents/${documentId}/upload`;
      await axios.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Ανέβηκαν ${files.length} αρχεία`);
      if (isGeneral) {
        fetchGeneralDocuments();
      } else {
        fetchProjectDetails();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα ανεβάσματος');
    } finally {
      setUploadingDocId(null);
    }
  };

  const handleDeleteFile = async (documentId, isGeneral = false, fileIndex = -1) => {
    try {
      const endpoint = isGeneral
        ? `${API}/implementation/general-documents/${documentId}/file?file_index=${fileIndex}`
        : `${API}/implementation/documents/${documentId}/file?file_index=${fileIndex}`;
      await axios.delete(endpoint);
      toast.success('Το αρχείο διαγράφηκε');
      if (isGeneral) {
        fetchGeneralDocuments();
      } else {
        fetchProjectDetails();
      }
    } catch (error) {
      toast.error('Σφάλμα διαγραφής');
    }
  };

  // Mark document as reviewed by owner (removes green highlight)
  const handleMarkReviewed = async (documentId, isGeneral = false) => {
    try {
      const endpoint = isGeneral
        ? `${API}/implementation/general-documents/${documentId}/mark-reviewed`
        : `${API}/implementation/documents/${documentId}/mark-reviewed`;
      await axios.post(endpoint);
      toast.success('Το έγγραφο σημειώθηκε ως ελεγμένο');
      if (isGeneral) {
        fetchGeneralDocuments();
      } else {
        fetchProjectDetails();
      }
    } catch (error) {
      toast.error('Σφάλμα κατά τη σημείωση');
    }
  };

  const handleDownload = async (documentId, isGeneral = false, fileIndex = 0, fileName = null) => {
    try {
      const endpoint = isGeneral
        ? `${API}/implementation/general-documents/${documentId}/download?file_index=${fileIndex}`
        : `${API}/implementation/documents/${documentId}/download?file_index=${fileIndex}`;
      // Manually handle non-2xx so we can parse error blobs
      const response = await axios.get(endpoint, {
        responseType: 'blob',
        validateStatus: (s) => s >= 200 && s < 300
      });
      // Extract filename from Content-Disposition header if not provided
      let downloadName = fileName;
      if (!downloadName) {
        const disposition = response.headers['content-disposition'];
        if (disposition) {
          const utf8Match = disposition.match(/filename\*=UTF-8''(.+)/);
          if (utf8Match) downloadName = decodeURIComponent(utf8Match[1]);
        }
      }
      if (!downloadName) downloadName = 'document.pdf';
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Download error:', error);
      let msg = 'Σφάλμα λήψης αρχείου';
      const status = error?.response?.status;
      // Try to read the actual server error message even if it came as a blob
      try {
        const data = error?.response?.data;
        if (data instanceof Blob) {
          const text = await data.text();
          try {
            const parsed = JSON.parse(text);
            if (parsed?.detail) msg = `${parsed.detail}${status ? ` (${status})` : ''}`;
          } catch {
            if (text && text.length < 200) msg = `${text}${status ? ` (${status})` : ''}`;
          }
        }
      } catch {}
      if (msg === 'Σφάλμα λήψης αρχείου') {
        if (status === 404) msg = 'Το αρχείο δεν βρέθηκε στον server (404)';
        else if (status === 401 || status === 403) msg = 'Δεν έχετε άδεια πρόσβασης στο αρχείο';
        else if (status >= 500) msg = `Σφάλμα διακομιστή (${status})`;
        else if (error?.message) msg = `Σφάλμα λήψης: ${error.message}`;
      }
      toast.error(msg);
    }
  };

  // Helper to get file name from path
  const getFileName = (filePath) => {
    if (!filePath) return 'document.pdf';
    // R2 paths: r2://projects/.../uuid_filename.ext
    if (filePath.startsWith('r2://')) {
      const parts = filePath.split('/');
      const fullName = parts[parts.length - 1];
      const idx = fullName.indexOf('_');
      return idx > 0 ? fullName.substring(idx + 1) : fullName;
    }
    // GridFS paths with filename: gridfs:objectid:gen_docid_fileid_filename
    if (filePath.startsWith('gridfs:') && filePath.split(':').length >= 3) {
      const parts = filePath.split(':');
      const rawName = parts.slice(2).join(':');
      const nameParts = rawName.split('_');
      return nameParts.length > 3 ? nameParts.slice(3).join('_') : rawName;
    }
    // GridFS paths without filename: gridfs://objectid
    if (filePath.startsWith('gridfs:')) {
      return null;
    }
    // Local file paths: /path/uuid_filename.ext
    const parts = filePath.split('/');
    const fullName = parts[parts.length - 1];
    const idx = fullName.indexOf('_');
    return idx > 0 ? fullName.substring(idx + 1) : fullName;
  };

  // Helper to get file_paths from document (with backward compatibility)
  const getFilePaths = (doc) => {
    if (doc.file_paths && doc.file_paths.length > 0) {
      return doc.file_paths;
    }
    if (doc.file_path) {
      return [doc.file_path];
    }
    return [];
  };

  // Expiry tracking functions
  const openExpiryDialog = (doc) => {
    setExpiryEditingDoc(doc);
    setExpiryIssueDate(doc.issue_date || '');
    setExpiryExpiryDate(doc.expiry_date || '');
    setExpiryDialogOpen(true);
  };

  const handleSaveExpiry = async () => {
    if (!expiryEditingDoc) return;
    setExpirySaving(true);
    try {
      await axios.put(`${API}/implementation/documents/${expiryEditingDoc.document_id}/expiry`, {
        issue_date: expiryIssueDate || null,
        expiry_date: expiryExpiryDate || null,
        expiry_tracking: !!expiryExpiryDate
      });
      toast.success('Οι ημερομηνίες αποθηκεύτηκαν');
      setExpiryDialogOpen(false);
      fetchProjectDetails();
      fetchGeneralDocuments();
    } catch (error) {
      toast.error('Σφάλμα αποθήκευσης ημερομηνιών');
    } finally {
      setExpirySaving(false);
    }
  };

  const handleClearExpiry = async () => {
    if (!expiryEditingDoc) return;
    setExpirySaving(true);
    try {
      await axios.put(`${API}/implementation/documents/${expiryEditingDoc.document_id}/expiry`, {
        issue_date: null,
        expiry_date: null,
        expiry_tracking: false
      });
      toast.success('Οι ημερομηνίες διαγράφηκαν');
      setExpiryDialogOpen(false);
      fetchProjectDetails();
      fetchGeneralDocuments();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής ημερομηνιών');
    } finally {
      setExpirySaving(false);
    }
  };

  const getExpiryInfo = (doc) => {
    if (!doc.expiry_date) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(doc.expiry_date);
    expiry.setHours(0, 0, 0, 0);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return { daysLeft: diffDays, expired: diffDays < 0, expiringSoon: diffDays >= 0 && diffDays <= 30 };
  };

  const handleAddSupplier = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/implementation/projects/${projectId}/suppliers`, newSupplier);
      toast.success('Ο προμηθευτής προστέθηκε');
      setNewSupplier({ name: '', email: '' });
      setSupplierDialogOpen(false);
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSupplier = async (supplierId) => {
    try {
      await axios.delete(`${API}/implementation/projects/${projectId}/suppliers/${supplierId}`);
      toast.success('Ο προμηθευτής αφαιρέθηκε');
      fetchSuppliers();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής');
    }
  };

  const openEditSupplier = (supplier) => {
    setEditingSupplier({ ...supplier });
    setEditSupplierDialogOpen(true);
  };

  const handleUpdateSupplier = async (e) => {
    e.preventDefault();
    if (!editingSupplier) return;
    setLoading(true);
    try {
      await axios.put(
        `${API}/implementation/projects/${projectId}/suppliers/${editingSupplier.supplier_id}`,
        {
          name: editingSupplier.name,
          email: editingSupplier.email,
          phone: editingSupplier.phone,
          full_name: editingSupplier.full_name
        }
      );
      toast.success('Ο συνεργάτης ενημερώθηκε');
      setEditSupplierDialogOpen(false);
      setEditingSupplier(null);
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα ενημέρωσης');
    } finally {
      setLoading(false);
    }
  };

  const fetchImplTemplates = async () => {
    try {
      const response = await axios.get(`${API}/implementation-templates?user_id=${user.user_id}`);
      setAvailableTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleReview = async () => {
    if (!reviewingDocument) return;
    try {
      const endpoint = reviewType === 'general'
        ? `${API}/implementation/general-documents/${reviewingDocument.document_id}/review`
        : `${API}/implementation/documents/${reviewingDocument.document_id}/review`;
      await axios.post(endpoint, reviewData);
      toast.success('Η αξιολόγηση αποθηκεύτηκε');
      setReviewDialogOpen(false);
      setReviewingDocument(null);
      if (reviewType === 'general') {
        fetchGeneralDocuments();
      } else {
        fetchProjectDetails();
      }
    } catch (error) {
      toast.error('Σφάλμα αποθήκευσης');
    }
  };

  const handleAssign = async () => {
    if (!assigningDocument) return;
    try {
      const endpoint = assignType === 'general'
        ? `${API}/implementation/general-documents/${assigningDocument.document_id}/assign`
        : `${API}/implementation/documents/${assigningDocument.document_id}/assign`;
      await axios.post(endpoint, selectedSuppliers);
      toast.success('Η ανάθεση αποθηκεύτηκε');
      setAssignDialogOpen(false);
      setAssigningDocument(null);
      setSelectedSuppliers([]);
      if (assignType === 'general') {
        fetchGeneralDocuments();
      } else {
        fetchProjectDetails();
      }
    } catch (error) {
      toast.error('Σφάλμα ανάθεσης');
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await axios.post(`${API}/implementation/projects/${projectId}/send-reminders`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα αποστολής');
    } finally {
      setSendingReminders(false);
    }
  };

  const handleBackup = async () => {
    setBackupLoading(true);
    try {
      // Check files with retries
      let check = null;
      let retries = 3;
      
      for (let attempt = 1; attempt <= retries; attempt++) {
        const checkResponse = await axios.get(`${API}/implementation/projects/${projectId}/backup-check`);
        check = checkResponse.data;
        
        if (check.files_missing === 0) {
          break; // All files found
        }
        
        if (attempt < retries) {
          // Wait and retry
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      if (check.files_found === 0) {
        toast.error('Δεν υπάρχουν αρχεία για backup');
        setBackupLoading(false);
        return;
      }
      
      if (check.files_missing > 0) {
        // Show which documents have missing files
        const missingInvoiceDocs = check.invoice_documents
          .filter(d => d.files_missing.length > 0)
          .map(d => `${d.invoice}: ${d.document_name}`);
        const missingGeneralDocs = check.general_documents
          .filter(d => d.files_missing.length > 0)
          .map(d => d.document_name);
        
        const allMissing = [...missingInvoiceDocs, ...missingGeneralDocs].join(', ');
        
        toast.error(
          `Σφάλμα: Λείπουν ${check.files_missing} αρχεία από: ${allMissing}. Παρακαλώ ανεβάστε τα ξανά.`,
          { duration: 8000 }
        );
        setBackupLoading(false);
        return;
      }
      
      // All files exist - proceed with backup
      const response = await axios.get(`${API}/implementation/projects/${projectId}/backup`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.setAttribute('download', `backup_impl_${timestamp}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Backup ολοκληρώθηκε! ${check.files_found} αρχεία`);
    } catch (error) {
      toast.error('Σφάλμα δημιουργίας backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleR2Relink = async () => {
    setRelinkLoading(true);
    setRelinkResult(null);
    setRelinkStep('preview');
    setRelinkDialogOpen(true);
    try {
      // First: diagnose to show user complete file status
      const diagResp = await axios.get(
        `${API}/admin/diagnose-project-files?project_id=${projectId}`
      );
      // Then: dry-run relink to see what can be recovered
      const relinkResp = await axios.post(
        `${API}/admin/relink-r2-files?project_id=${projectId}&dry_run=true`
      );
      setRelinkResult({ ...relinkResp.data, diagnostic: diagResp.data });
    } catch (error) {
      console.error('R2 relink error:', error);
      toast.error(error.response?.data?.detail || 'Σφάλμα συγχρονισμού με R2');
      setRelinkDialogOpen(false);
    } finally {
      setRelinkLoading(false);
    }
  };

  const handleR2RelinkApply = async () => {
    setRelinkLoading(true);
    try {
      const resp = await axios.post(
        `${API}/admin/relink-r2-files?project_id=${projectId}&dry_run=false`
      );
      setRelinkResult(resp.data);
      setRelinkStep('applied');
      toast.success(`Συγχρονίστηκαν ${resp.data.documents_relinked} έγγραφα από το R2`);
      // Refresh project data so the UI shows the recovered files
      await fetchProjectDetails();
    } catch (error) {
      console.error('R2 relink apply error:', error);
      toast.error(error.response?.data?.detail || 'Σφάλμα εφαρμογής συγχρονισμού');
    } finally {
      setRelinkLoading(false);
    }
  };

  const copyShareLink = () => {
    if (project?.share_id) {
      const link = `${window.location.origin}/impl-upload/${project.share_id}`;
      navigator.clipboard.writeText(link);
      toast.success('Ο σύνδεσμος αντιγράφηκε');
    }
  };

  const copyProgressLink = () => {
    const progressShareId = project?.progress_share_id || project?.project_id;
    const progressUrl = `${window.location.origin}/progress/impl/${progressShareId}`;
    navigator.clipboard.writeText(progressUrl);
    toast.success('Ο σύνδεσμος προόδου αντιγράφηκε');
  };

  const handleSaveNotificationSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/implementation/projects/${projectId}`, notificationSettings);
      toast.success('Οι ρυθμίσεις ειδοποιήσεων αποθηκεύτηκαν');
      setNotificationDialogOpen(false);
      fetchProjectDetails();
    } catch (error) {
      toast.error('Σφάλμα αποθήκευσης ρυθμίσεων');
    } finally {
      setLoading(false);
    }
  };

  const getSupplierName = (supplierId) => {
    const supplier = suppliers.find(s => s.supplier_id === supplierId);
    return supplier ? supplier.name : supplierId;
  };

  const getCategoryName = (categoryKey) => {
    const cat = expenseCategories.find(c => c.key === categoryKey);
    return cat ? cat.name : categoryKey;
  };

  const getGeneralCategoryName = (categoryKey) => {
    const names = {
      'general': 'Οικονομικό Αντικείμενο - Γενικά',
      'accounting': 'Λογιστικά / Εξόφληση',
      'private_participation': 'Ιδιωτική Συμμετοχή'
    };
    return names[categoryKey] || categoryKey;
  };

  const renderDocumentRow = (doc, isGeneral = false) => {
    const filePaths = getFilePaths(doc);
    const hasFiles = filePaths.length > 0;
    const canAddMore = filePaths.length < 20;
    const isRejected = doc.review_status === 'rejected';
    const isApproved = doc.review_status === 'approved';
    
    return (
      <div
        key={doc.document_id}
        className={`flex items-center justify-between p-3 border rounded-md transition-colors ${
          isRejected ? 'border-rose-300 bg-rose-50' : doc.needs_owner_review ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'
        }`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${isRejected ? 'text-rose-900' : doc.needs_owner_review ? 'text-emerald-700' : 'text-slate-900'}`}>
              {doc.document_name}
            </span>
            {doc.needs_owner_review && (
              <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Νέο</span>
            )}
            {filePaths.length > 1 && (
              <span className="text-xs text-slate-500">({filePaths.length} αρχεία)</span>
            )}
            {isApproved && <CheckCircle className="h-4 w-4 text-emerald-500" />}
            {isRejected && <XCircle className="h-4 w-4 text-rose-500" />}
          </div>
          
          {/* Show note if exists */}
          {doc.note && (
            <div className="text-xs text-blue-600 mt-1 flex items-center gap-1">
              <MessageSquare className="h-3 w-3" />
              <span className="truncate max-w-[200px]">{doc.note}</span>
            </div>
          )}
          
          {/* Expiry tracking */}
          <div className="mt-1 flex items-center gap-1.5">
            <button
              onClick={() => openExpiryDialog(doc)}
              className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded transition-colors ${
                doc.expiry_date
                  ? getExpiryInfo(doc)?.expired
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : getExpiryInfo(doc)?.expiringSoon
                      ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                      : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
              }`}
              title="Ημερομηνίες Έκδοσης / Λήξης"
              data-testid={`expiry-button-${doc.document_id}`}
            >
              <Clock className="h-3 w-3" />
              {doc.expiry_date ? (
                (() => {
                  const info = getExpiryInfo(doc);
                  if (info.expired) return `Έληξε (${Math.abs(info.daysLeft)} ημ.)`;
                  if (info.daysLeft === 0) return 'Λήγει σήμερα';
                  return `${info.daysLeft} ημ.`;
                })()
              ) : (
                <span className="opacity-70">Λήξη</span>
              )}
            </button>
          </div>
          
          {/* File list */}
          {hasFiles && (
            <div className="mt-1 space-y-1">
              {filePaths.map((fp, idx) => (
                <div key={idx} className="flex items-center gap-1 text-xs bg-slate-100 px-2 py-1 rounded">
                  <span className="truncate max-w-[150px]" title={getFileName(fp)}>
                    {getFileName(fp)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 text-emerald-600 hover:text-emerald-700"
                    onClick={() => handleDownload(doc.document_id, isGeneral, idx, getFileName(fp))}
                  >
                    <Download className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 text-rose-600 hover:text-rose-700"
                    onClick={() => handleDeleteFile(doc.document_id, isGeneral, idx)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          
          {doc.assigned_to && doc.assigned_to.length > 0 && (
            <div className="text-xs text-slate-500 mt-1">
              Ανάθεση: {doc.assigned_to.map(id => getSupplierName(id)).join(', ')}
            </div>
          )}
          
          {isRejected && doc.review_comment && (
            <div className="text-xs text-rose-600 mt-1 bg-rose-100 rounded px-2 py-1">
              {doc.review_comment}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 ml-4">
          {/* Upload/Add more */}
          <input
            type="file"
            id={`file-${doc.document_id}`}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.heic,.heif,.webp,.tiff"
            multiple
            className="hidden"
            onChange={(e) => handleUpload(doc.document_id, e.target.files, isGeneral)}
          />
          
          {/* Mark as reviewed button - only show if needs_owner_review */}
          {doc.needs_owner_review && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleMarkReviewed(doc.document_id, isGeneral)}
              className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
              title="Σημείωση ως ελεγμένο"
            >
              <Check className="h-3 w-3 mr-1" />
              Ελέγχθηκε
            </Button>
          )}
          
          {canAddMore && (
            <Button
              size="sm"
              variant={hasFiles ? "outline" : "default"}
              onClick={() => document.getElementById(`file-${doc.document_id}`).click()}
              disabled={uploadingDocId === doc.document_id}
              className={hasFiles ? 'text-blue-600 border-blue-300' : 'bg-slate-900 hover:bg-slate-800'}
            >
              <Upload className="h-3 w-3 mr-1" />
              {uploadingDocId === doc.document_id ? '...' : hasFiles ? `+Αρχείο (${20-filePaths.length})` : 'Ανέβασμα (έως 20)'}
            </Button>
          )}

          {/* Delete all files */}
          {hasFiles && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleDeleteFile(doc.document_id, isGeneral, -1)}
              className="text-rose-500"
              title="Διαγραφή όλων"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}

          {/* Edit */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setEditingDocument(doc);
              setEditDocName(doc.document_name);
              setEditDocDialogOpen(true);
            }}
            className="text-blue-600"
          >
            <Edit className="h-3 w-3" />
          </Button>

          {/* Assign */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setAssigningDocument(doc);
              setAssignType(isGeneral ? 'general' : 'invoice');
              setSelectedSuppliers(doc.assigned_to || []);
              setAssignDialogOpen(true);
            }}
            className="text-purple-600"
          >
            <Users className="h-3 w-3" />
          </Button>

          {/* Review */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setReviewingDocument(doc);
              setReviewType(isGeneral ? 'general' : 'invoice');
              setReviewData({ status: 'approved', comment: '' });
              setReviewDialogOpen(true);
            }}
            className="text-amber-600"
          >
            <MessageSquare className="h-3 w-3" />
          </Button>

          {/* Note */}
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={() => openNoteDialog(doc, isGeneral)}
            className={doc.note ? 'text-blue-600' : 'text-slate-400'}
            title={doc.note ? 'Επεξεργασία σημείωσης' : 'Προσθήκη σημείωσης'}
          >
            <Edit className="h-3 w-3" />
          </Button>

          {/* Delete */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="ghost" className="text-rose-600">
                <Trash2 className="h-3 w-3" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Διαγραφή Δικαιολογητικού</AlertDialogTitle>
                <AlertDialogDescription>
                  Είστε σίγουροι; Το αρχείο θα διαγραφεί οριστικά.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => isGeneral ? handleDeleteGeneralDocument(doc.document_id) : handleDeleteDocument(doc.document_id)}
                  className="bg-rose-600 hover:bg-rose-700"
                >
                  Διαγραφή
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {/* Upload Date */}
        {doc.uploaded_at && (
          <div className="ml-4 text-right border-l border-slate-200 pl-4">
            <div className="text-xs text-slate-500">Ανάρτηση:</div>
            <div className="text-sm text-slate-700">
              {new Date(doc.uploaded_at).toLocaleDateString('el-GR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              })}
            </div>
            <div className="text-xs text-slate-400">
              {new Date(doc.uploaded_at).toLocaleTimeString('el-GR', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
            {doc.uploaded_by && (
              <div className="text-xs text-slate-500 truncate max-w-[100px]" title={doc.uploaded_by}>
                {doc.uploaded_by}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (!project) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/implementation')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Πίσω
              </Button>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-slate-900">{project.project_title}</h1>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={openEditProjectDialog}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-slate-600">Πελάτης: {project.client_name}</p>
              {project.deadline && (
                <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                  <Clock className="h-4 w-4 text-orange-500" />
                  Προθεσμία: {new Date(project.deadline).toLocaleDateString('el-GR')}
                </p>
              )}
              {(project.total_budget || project.total_budget_vat) && (
                <p className="text-sm text-emerald-600 flex items-center gap-1 mt-1" data-testid="budget-summary">
                  <FileBarChart className="h-4 w-4" />
                  Προϋπ/σμός: €{((project.total_budget || 0) + (project.total_budget_vat || 0)).toLocaleString('el-GR', { minimumFractionDigits: 2 })}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleManualRefresh} title="Ανανέωση δεδομένων">
                <RefreshCw className="h-4 w-4 mr-2" />
                Ανανέωση
              </Button>
              <Button variant="outline" size="sm" onClick={copyShareLink}>
                <LinkIcon className="h-4 w-4 mr-2" />
                Αντιγραφή Συνδέσμου
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={copyProgressLink}
                className="text-cyan-600 border-cyan-300 hover:bg-cyan-50"
              >
                <Eye className="h-4 w-4 mr-2" />
                Κοινοποίηση Προόδου
              </Button>
              <Button variant="outline" size="sm" onClick={handleBackup} disabled={backupLoading}>
                <Download className={`h-4 w-4 mr-2 ${backupLoading ? 'animate-bounce' : ''}`} />
                Backup
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleR2Relink} 
                disabled={relinkLoading}
                className="text-blue-600 border-blue-300 hover:bg-blue-50"
                data-testid="r2-relink-btn"
                title="Επανασύνδεση αρχείων από το Cloudflare R2"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${relinkLoading ? 'animate-spin' : ''}`} />
                Συγχρονισμός με R2
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchFinancialReport}
                disabled={loadingReport}
                className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                data-testid="financial-report-btn"
              >
                <FileBarChart className={`h-4 w-4 mr-2 ${loadingReport ? 'animate-spin' : ''}`} />
                Αναφορά
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setNotificationSettings({
                    notification_enabled: project.notification_enabled ?? true,
                    notification_interval_days: project.notification_interval_days ?? 3,
                    advisor_escalation_enabled: project.advisor_escalation_enabled ?? false,
                    advisor_escalation_threshold: project.advisor_escalation_threshold ?? 3
                  });
                  setNotificationDialogOpen(true);
                }}
                className={project.notification_enabled ? 'text-emerald-600 border-emerald-300' : 'text-slate-400'}
              >
                {project.notification_enabled ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
              </Button>
              <Button variant="ghost" size="sm" onClick={onLogout}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* General Documents Section */}
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
              <div
                className="flex items-center justify-between p-4 border-b border-slate-200 cursor-pointer"
                onClick={() => setGeneralDocsExpanded(!generalDocsExpanded)}
              >
                <div className="flex items-center gap-2">
                  <FolderOpen className="h-5 w-5 text-blue-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Γενικά Δικαιολογητικά</h2>
                  <span className="text-sm text-slate-500">
                    ({generalDocuments.reduce((acc, cat) => acc + cat.documents.length, 0)})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {generalDocuments.length === 0 && (
                    <Button size="sm" onClick={(e) => { e.stopPropagation(); handleInitGeneralDocuments(); }}>
                      <Plus className="h-4 w-4 mr-1" />
                      Αρχικοποίηση
                    </Button>
                  )}
                  <Dialog open={generalDocDialogOpen} onOpenChange={setGeneralDocDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" onClick={(e) => e.stopPropagation()}>
                        <Plus className="h-4 w-4 mr-1" />
                        Προσθήκη
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Νέο Γενικό Δικαιολογητικό</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddGeneralDocument} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Κατηγορία</Label>
                          <Select
                            value={newGeneralDoc.category}
                            onValueChange={(v) => setNewGeneralDoc({...newGeneralDoc, category: v})}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="general">Οικονομικό Αντικείμενο - Γενικά</SelectItem>
                              <SelectItem value="accounting">Λογιστικά / Εξόφληση</SelectItem>
                              <SelectItem value="private_participation">Ιδιωτική Συμμετοχή</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Όνομα Δικαιολογητικού</Label>
                          <Input
                            value={newGeneralDoc.document_name}
                            onChange={(e) => setNewGeneralDoc({...newGeneralDoc, document_name: e.target.value})}
                            required
                          />
                        </div>
                        <Button type="submit" className="w-full" disabled={loading}>
                          {loading ? 'Προσθήκη...' : 'Προσθήκη'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                  {generalDocsExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
              </div>
              
              {generalDocsExpanded && (
                <div className="p-4 space-y-4">
                  {generalDocuments.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">
                      Πατήστε &ldquo;Αρχικοποίηση&rdquo; για να δημιουργήσετε τα γενικά δικαιολογητικά
                    </p>
                  ) : (
                    generalDocuments.map((category) => (
                      <div key={category.category} className="space-y-2">
                        <h3 className="text-sm font-medium text-slate-700 bg-slate-100 px-3 py-2 rounded">
                          {category.name} ({category.documents.length})
                        </h3>
                        <div className="space-y-2 pl-2">
                          {category.documents.map((doc) => renderDocumentRow(doc, true))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Invoices Section */}
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
              <div
                className="flex items-center justify-between p-4 border-b border-slate-200 cursor-pointer"
                onClick={() => setInvoicesExpanded(!invoicesExpanded)}
              >
                <div className="flex items-center gap-2">
                  <Receipt className="h-5 w-5 text-emerald-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Τιμολόγια</h2>
                  <span className="text-sm text-slate-500">({invoices.length})</span>
                </div>
                <div className="flex items-center gap-2">
                  <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" onClick={(e) => e.stopPropagation()}>
                        <Plus className="h-4 w-4 mr-1" />
                        Νέο Τιμολόγιο
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Νέο Τιμολόγιο</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddInvoice} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Αριθμός Τιμολογίου</Label>
                          <Input
                            value={newInvoice.invoice_number}
                            onChange={(e) => setNewInvoice({...newInvoice, invoice_number: e.target.value})}
                            placeholder="π.χ. ΤΙΜ-001"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Κατηγορία Δαπάνης</Label>
                          <Select
                            value={newInvoice.expense_category}
                            onValueChange={(v) => setNewInvoice({...newInvoice, expense_category: v})}
                          >
                            <SelectTrigger><SelectValue placeholder="Επιλέξτε κατηγορία..." /></SelectTrigger>
                            <SelectContent>
                              {expenseCategories.filter(cat => cat.key).map((cat) => (
                                <SelectItem key={cat.key} value={cat.key}>
                                  {cat.name} ({cat.document_count} δικ.)
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-slate-500">
                            Τα δικαιολογητικά της κατηγορίας θα προστεθούν αυτόματα
                          </p>
                        </div>
                        
                        {/* Invoice Amounts */}
                        <div className="border-t pt-4 mt-4">
                          <h4 className="font-medium text-slate-900 mb-3">Ποσά Τιμολογίου (προαιρετικά)</h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Καθαρό Ποσό (€)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={newInvoice.net_amount}
                                onChange={(e) => setNewInvoice({...newInvoice, net_amount: e.target.value})}
                                placeholder="0.00"
                                data-testid="invoice-net-amount"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>ΦΠΑ (€)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={newInvoice.vat_amount}
                                onChange={(e) => setNewInvoice({...newInvoice, vat_amount: e.target.value})}
                                placeholder="0.00"
                                data-testid="invoice-vat-amount"
                              />
                            </div>
                          </div>
                          {(newInvoice.net_amount || newInvoice.vat_amount) && (
                            <p className="text-sm text-slate-500 mt-2">
                              Σύνολο: €{((parseFloat(newInvoice.net_amount) || 0) + (parseFloat(newInvoice.vat_amount) || 0)).toFixed(2)}
                            </p>
                          )}
                        </div>
                        
                        <Button type="submit" className="w-full" disabled={loading}>
                          {loading ? 'Δημιουργία...' : 'Δημιουργία Τιμολογίου'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                  {invoicesExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
              </div>

              {invoicesExpanded && (
                <div className="p-4 space-y-4">
                  {/* Search */}
                  {invoices.length > 0 && (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Αναζήτηση τιμολογίου..."
                        value={invoiceSearch}
                        onChange={(e) => setInvoiceSearch(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  )}
                  
                  {invoices.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">
                      Δεν υπάρχουν τιμολόγια. Προσθέστε το πρώτο τιμολόγιο.
                    </p>
                  ) : (
                    invoices
                      .filter(inv => 
                        !invoiceSearch || 
                        inv.invoice_number?.toLowerCase().includes(invoiceSearch.toLowerCase()) ||
                        getCategoryName(inv.expense_category)?.toLowerCase().includes(invoiceSearch.toLowerCase())
                      )
                      .map((invoice) => (
                      <div key={invoice.invoice_id} className="border border-slate-200 rounded-lg">
                        <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                          <div className="flex-1">
                            <span className="font-medium text-slate-900">{invoice.invoice_number}</span>
                            <span className="text-xs text-slate-500 ml-2">
                              {getCategoryName(invoice.expense_category)}
                            </span>
                            {(invoice.net_amount || invoice.vat_amount) && (
                              <span className="text-xs text-emerald-600 ml-2">
                                €{((invoice.net_amount || 0) + (invoice.vat_amount || 0)).toLocaleString('el-GR', { minimumFractionDigits: 2 })}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingInvoice({
                                  ...invoice,
                                  net_amount: invoice.net_amount || '',
                                  vat_amount: invoice.vat_amount || ''
                                });
                                setEditInvoiceDialogOpen(true);
                              }}
                              className="text-cyan-600"
                            >
                              <Edit className="h-3 w-3 mr-1" />
                              Επεξεργασία
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedInvoiceForDoc(invoice);
                                setCustomDocDialogOpen(true);
                              }}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Προσθήκη
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="ghost" className="text-rose-600">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Διαγραφή Τιμολογίου</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Θα διαγραφούν όλα τα δικαιολογητικά του τιμολογίου.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteInvoice(invoice.invoice_id)}
                                    className="bg-rose-600 hover:bg-rose-700"
                                  >
                                    Διαγραφή
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                        <div className="p-4 space-y-2">
                          {invoice.documents && invoice.documents.length > 0 ? (
                            invoice.documents.map((doc) => renderDocumentRow(doc, false))
                          ) : (
                            <p className="text-sm text-slate-500 text-center py-2">
                              Δεν υπάρχουν δικαιολογητικά
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Suppliers */}
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
              <div className="flex items-center justify-between p-4 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-purple-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Συνεργάτες</h2>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSendReminders}
                    disabled={sendingReminders || suppliers.length === 0}
                  >
                    <Send className={`h-4 w-4 mr-1 ${sendingReminders ? 'animate-pulse' : ''}`} />
                    Υπενθύμιση
                  </Button>
                  <Dialog open={supplierDialogOpen} onOpenChange={setSupplierDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <UserPlus className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Νέος Συνεργάτης</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddSupplier} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Ονοματεπώνυμο</Label>
                          <Input
                            value={newSupplier.name}
                            onChange={(e) => setNewSupplier({...newSupplier, name: e.target.value})}
                            placeholder="π.χ. Γιάννης Παπαδόπουλος"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Email</Label>
                          <Input
                            type="email"
                            value={newSupplier.email}
                            onChange={(e) => setNewSupplier({...newSupplier, email: e.target.value})}
                            placeholder="email@example.com"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Τηλέφωνο</Label>
                          <Input
                            type="tel"
                            value={newSupplier.phone}
                            onChange={(e) => setNewSupplier({...newSupplier, phone: e.target.value})}
                            placeholder="69xxxxxxxx"
                          />
                        </div>
                        <Button type="submit" className="w-full" disabled={loading}>
                          {loading ? 'Προσθήκη...' : 'Προσθήκη'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
              <div className="p-4 space-y-2">
                {suppliers.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">
                    Δεν υπάρχουν προμηθευτές
                  </p>
                ) : (
                  suppliers.map((supplier) => (
                    <div
                      key={supplier.supplier_id}
                      className="flex items-center justify-between p-3 border border-slate-200 rounded-md"
                    >
                      <div>
                        <div className="font-medium text-slate-900">{supplier.full_name || supplier.name}</div>
                        <div className="text-xs text-slate-500">{supplier.email}</div>
                        {supplier.phone && (
                          <div className="text-xs text-slate-400">{supplier.phone}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-slate-400 hover:text-blue-600"
                          onClick={() => openEditSupplier(supplier)}
                          data-testid={`edit-supplier-${supplier.supplier_id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" className="text-rose-600">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Αφαίρεση Συνεργάτη</AlertDialogTitle>
                              <AlertDialogDescription>
                                Είστε σίγουροι;
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteSupplier(supplier.supplier_id)}
                                className="bg-rose-600 hover:bg-rose-700"
                              >
                                Αφαίρεση
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Document Dialog */}
      <Dialog open={customDocDialogOpen} onOpenChange={setCustomDocDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Προσθήκη Δικαιολογητικού</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddCustomDocument} className="space-y-4">
            <div className="space-y-2">
              <Label>Όνομα Δικαιολογητικού</Label>
              <Input
                value={customDocName}
                onChange={(e) => setCustomDocName(e.target.value)}
                placeholder="π.χ. Επιπλέον Βεβαίωση"
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Προσθήκη...' : 'Προσθήκη'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Document Dialog */}
      <Dialog open={editDocDialogOpen} onOpenChange={setEditDocDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Επεξεργασία Δικαιολογητικού</DialogTitle>
          </DialogHeader>
          <form onSubmit={editingDocument?.category ? handleEditGeneralDocument : handleEditDocument} className="space-y-4">
            <div className="space-y-2">
              <Label>Όνομα</Label>
              <Input
                value={editDocName}
                onChange={(e) => setEditDocName(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full">
              Αποθήκευση
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Invoice Dialog */}
      <Dialog open={editInvoiceDialogOpen} onOpenChange={setEditInvoiceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Επεξεργασία Τιμολογίου</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditInvoice} className="space-y-4">
            <div className="space-y-2">
              <Label>Αριθμός Τιμολογίου</Label>
              <Input
                value={editingInvoice?.invoice_number || ''}
                onChange={(e) => setEditingInvoice({ ...editingInvoice, invoice_number: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Κατηγορία Δαπάνης</Label>
              <Select
                value={editingInvoice?.expense_category || ''}
                onValueChange={(v) => setEditingInvoice({ ...editingInvoice, expense_category: v })}
              >
                <SelectTrigger><SelectValue placeholder="Επιλέξτε κατηγορία..." /></SelectTrigger>
                <SelectContent>
                  {expenseCategories.filter(cat => cat.key).map((cat) => (
                    <SelectItem key={cat.key} value={cat.key}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Καθαρό Ποσό (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editingInvoice?.net_amount || ''}
                  onChange={(e) => setEditingInvoice({ ...editingInvoice, net_amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>ΦΠΑ (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editingInvoice?.vat_amount || ''}
                  onChange={(e) => setEditingInvoice({ ...editingInvoice, vat_amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>
            {editingInvoice && (editingInvoice.net_amount || editingInvoice.vat_amount) && (
              <p className="text-sm text-slate-500">
                Σύνολο: €{((parseFloat(editingInvoice.net_amount) || 0) + (parseFloat(editingInvoice.vat_amount) || 0)).toFixed(2)}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Αποθήκευση...' : 'Αποθήκευση'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Αξιολόγηση Δικαιολογητικού</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-4">
              <Button
                variant={reviewData.status === 'approved' ? 'default' : 'outline'}
                onClick={() => setReviewData({...reviewData, status: 'approved'})}
                className={reviewData.status === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              >
                <Check className="h-4 w-4 mr-2" />
                Σωστό
              </Button>
              <Button
                variant={reviewData.status === 'rejected' ? 'default' : 'outline'}
                onClick={() => setReviewData({...reviewData, status: 'rejected'})}
                className={reviewData.status === 'rejected' ? 'bg-rose-600 hover:bg-rose-700' : ''}
              >
                <X className="h-4 w-4 mr-2" />
                Λάθος
              </Button>
            </div>
            <div className="space-y-2">
              <Label>Σχόλιο (προαιρετικό)</Label>
              <Textarea
                value={reviewData.comment}
                onChange={(e) => setReviewData({...reviewData, comment: e.target.value})}
                placeholder="Προσθέστε σχόλιο..."
              />
            </div>
            <Button onClick={handleReview} className="w-full">
              Αποθήκευση
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ανάθεση σε Συνεργάτες</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {suppliers.length === 0 ? (
              <p className="text-sm text-slate-500">Δεν υπάρχουν προμηθευτές</p>
            ) : (
              <div className="space-y-2">
                {suppliers.map((supplier) => (
                  <label
                    key={supplier.supplier_id}
                    className="flex items-center gap-3 p-3 border rounded-md cursor-pointer hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedSuppliers.includes(supplier.supplier_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedSuppliers([...selectedSuppliers, supplier.supplier_id]);
                        } else {
                          setSelectedSuppliers(selectedSuppliers.filter(id => id !== supplier.supplier_id));
                        }
                      }}
                      className="h-4 w-4"
                    />
                    <div>
                      <div className="font-medium">{supplier.name}</div>
                      <div className="text-xs text-slate-500">{supplier.email}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
            <Button onClick={handleAssign} className="w-full" disabled={suppliers.length === 0}>
              Αποθήκευση
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={editProjectDialogOpen} onOpenChange={setEditProjectDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Επεξεργασία Έργου
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-impl-project-name">Όνομα Έργου</Label>
              <Input
                id="edit-impl-project-name"
                value={editProjectName}
                onChange={(e) => setEditProjectName(e.target.value)}
                placeholder="π.χ. Ψηφιακός Μετασχηματισμός ΜΜΕ"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-impl-client-name">Όνομα Πελάτη</Label>
              <Input
                id="edit-impl-client-name"
                value={editClientName}
                onChange={(e) => setEditClientName(e.target.value)}
                placeholder="π.χ. Εταιρεία ΑΕ"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-impl-deadline">Προθεσμία</Label>
              <Input
                id="edit-impl-deadline"
                type="date"
                value={editDeadline ? editDeadline.split('T')[0] : ''}
                onChange={(e) => setEditDeadline(e.target.value)}
              />
            </div>
            
            {/* Budget Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-slate-900 mb-3">Πρότυπο Υλοποίησης</h4>
              {project?.template_id ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-md border border-slate-200">
                  <LinkIcon className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm text-slate-700">
                    Συνδεδεμένο: <strong>{availableTemplates.find(t => t.template_id === project.template_id)?.name || project.template_id}</strong>
                  </span>
                </div>
              ) : (
                <Select
                  value={editTemplateId || '_none'}
                  onValueChange={(value) => setEditTemplateId(value === '_none' ? '' : value)}
                >
                  <SelectTrigger data-testid="edit-impl-template-select">
                    <SelectValue placeholder="Επιλέξτε πρότυπο..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Χωρίς πρότυπο</SelectItem>
                    {availableTemplates.map((t) => (
                      <SelectItem key={t.template_id} value={t.template_id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {/* Budget Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-slate-900 mb-3">Προϋπολογισμός Έργου</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-total-budget">Καθαρό Ποσό (€)</Label>
                  <Input
                    id="edit-total-budget"
                    type="number"
                    step="0.01"
                    value={editTotalBudget}
                    onChange={(e) => setEditTotalBudget(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-total-budget-vat">ΦΠΑ (€)</Label>
                  <Input
                    id="edit-total-budget-vat"
                    type="number"
                    step="0.01"
                    value={editTotalBudgetVat}
                    onChange={(e) => setEditTotalBudgetVat(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </div>
              {(editTotalBudget || editTotalBudgetVat) && (
                <p className="text-sm text-slate-500 mt-2">
                  Σύνολο: €{((parseFloat(editTotalBudget) || 0) + (parseFloat(editTotalBudgetVat) || 0)).toFixed(2)}
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setEditProjectDialogOpen(false)}>
              Ακύρωση
            </Button>
            <Button onClick={handleEditProject} className="bg-slate-900 hover:bg-slate-800">
              Αποθήκευση
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Supplier Dialog */}
      <Dialog open={editSupplierDialogOpen} onOpenChange={setEditSupplierDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Επεξεργασία Συνεργάτη
            </DialogTitle>
          </DialogHeader>
          {editingSupplier && (
            <form onSubmit={handleUpdateSupplier} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-supplier-name">Ονοματεπώνυμο</Label>
                <Input
                  id="edit-supplier-name"
                  value={editingSupplier.name || ''}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, name: e.target.value })}
                  placeholder="π.χ. Γιάννης Παπαδόπουλος"
                  required
                  data-testid="edit-supplier-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-supplier-email">Email</Label>
                <Input
                  id="edit-supplier-email"
                  type="email"
                  value={editingSupplier.email || ''}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, email: e.target.value })}
                  placeholder="email@example.com"
                  required
                  data-testid="edit-supplier-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-supplier-phone">Τηλέφωνο</Label>
                <Input
                  id="edit-supplier-phone"
                  type="tel"
                  value={editingSupplier.phone || ''}
                  onChange={(e) => setEditingSupplier({ ...editingSupplier, phone: e.target.value })}
                  placeholder="69xxxxxxxx"
                  data-testid="edit-supplier-phone"
                />
              </div>
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={loading} data-testid="save-edit-supplier">
                {loading ? 'Αποθήκευση...' : 'Αποθήκευση Αλλαγών'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Financial Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-emerald-600" />
              Οικονομική Αναφορά Έργου
            </DialogTitle>
          </DialogHeader>
          {financialReport && (
            <div className="space-y-6">
              {/* Budget vs Actual Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h4 className="font-medium text-slate-700 mb-2">Προϋπολογισμός (Καθαρό)</h4>
                  <p className="text-2xl font-bold text-slate-900">
                    €{financialReport.budget?.total_net?.toLocaleString('el-GR', { minimumFractionDigits: 2 }) || '0.00'}
                  </p>
                </div>
                <div className={`rounded-lg p-4 border ${
                  financialReport.actual?.total_net > financialReport.budget?.total_net 
                    ? 'bg-rose-50 border-rose-300' 
                    : 'bg-emerald-50 border-emerald-200'
                }`}>
                  <h4 className={`font-medium mb-2 ${
                    financialReport.actual?.total_net > financialReport.budget?.total_net 
                      ? 'text-rose-700' 
                      : 'text-emerald-700'
                  }`}>Απορρόφηση (Καθαρό)</h4>
                  <p className={`text-2xl font-bold ${
                    financialReport.actual?.total_net > financialReport.budget?.total_net 
                      ? 'text-rose-700' 
                      : 'text-emerald-700'
                  }`}>
                    €{financialReport.actual?.total_net?.toLocaleString('el-GR', { minimumFractionDigits: 2 }) || '0.00'}
                  </p>
                  <p className={`text-sm ${
                    financialReport.actual?.total_net > financialReport.budget?.total_net 
                      ? 'text-rose-600' 
                      : 'text-emerald-600'
                  }`}>
                    {financialReport.actual?.percentage_used || 0}% του προϋπολογισμού
                  </p>
                </div>
              </div>

              {/* Budget Status */}
              {financialReport.budget?.total_net > 0 && (
                <div className={`p-4 rounded-lg border ${
                  financialReport.actual?.total_net > financialReport.budget?.total_net 
                    ? 'bg-rose-100 border-rose-300' 
                    : 'bg-blue-50 border-blue-200'
                }`}>
                  {financialReport.actual?.total_net > financialReport.budget?.total_net ? (
                    <div className="flex items-center justify-between text-rose-700">
                      <span className="font-medium">⚠️ Υπέρβαση Προϋπολογισμού:</span>
                      <span className="text-lg font-bold">
                        +€{(financialReport.actual?.total_net - financialReport.budget?.total_net).toLocaleString('el-GR', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between text-blue-700">
                      <span className="font-medium">Απομένουν ακόμα:</span>
                      <span className="text-lg font-bold">
                        €{(financialReport.budget?.total_net - financialReport.actual?.total_net).toLocaleString('el-GR', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Categories Breakdown */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Ανάλυση ανά Κατηγορία</h4>
                {financialReport.categories?.length > 0 ? (
                  <div className="space-y-3">
                    {financialReport.categories.map((cat, index) => (
                      <div key={index} className={`bg-white border rounded-lg p-4 ${cat.over_limit ? 'border-rose-300 bg-rose-50' : 'border-slate-200'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium text-slate-800">{cat.category}</h5>
                          <div className="flex items-center gap-2">
                            {cat.max_percentage > 0 && (
                              <span className={`text-xs px-2 py-0.5 rounded ${cat.over_limit ? 'bg-rose-100 text-rose-700' : 'bg-blue-100 text-blue-700'}`}>
                                Όριο: {cat.max_percentage}%
                              </span>
                            )}
                            <span className="text-sm font-medium text-emerald-600">{cat.percentage}%</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm mb-2">
                          <div>
                            <span className="text-slate-500">Καθαρό:</span>
                            <span className="ml-2 font-medium">€{cat.total_net?.toLocaleString('el-GR', { minimumFractionDigits: 2 })}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">ΦΠΑ:</span>
                            <span className="ml-2 font-medium">€{cat.total_vat?.toLocaleString('el-GR', { minimumFractionDigits: 2 })}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">Τιμολόγια:</span>
                            <span className="ml-2 font-medium">{cat.invoice_count}</span>
                          </div>
                        </div>
                        {/* Limit info */}
                        {cat.max_percentage > 0 && cat.limit_amount > 0 && (
                          <div className={`text-sm p-2 rounded ${cat.over_limit ? 'bg-rose-100 text-rose-700' : 'bg-blue-50 text-blue-700'}`}>
                            <div className="flex justify-between">
                              <span>Όριο κατηγορίας:</span>
                              <span className="font-medium">€{cat.limit_amount?.toLocaleString('el-GR', { minimumFractionDigits: 2 })}</span>
                            </div>
                            {cat.over_limit ? (
                              <div className="flex justify-between text-rose-700 font-medium">
                                <span>⚠️ Υπέρβαση:</span>
                                <span>+€{cat.over_amount?.toLocaleString('el-GR', { minimumFractionDigits: 2 })}</span>
                              </div>
                            ) : (
                              <div className="flex justify-between">
                                <span>Υπόλοιπο:</span>
                                <span className="font-medium text-emerald-600">€{cat.remaining?.toLocaleString('el-GR', { minimumFractionDigits: 2 })}</span>
                              </div>
                            )}
                          </div>
                        )}
                        {/* Progress bar */}
                        <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${cat.over_limit ? 'bg-rose-500' : 'bg-emerald-500'}`}
                            style={{ width: `${Math.min(cat.percentage, 100)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Δεν υπάρχουν τιμολόγια</p>
                )}
              </div>

              {/* Total Invoices */}
              <div className="text-sm text-slate-500 text-center border-t pt-4">
                Σύνολο τιμολογίων: {financialReport.invoice_count}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Σημείωση - {editingNoteDoc?.document_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Προσθέστε σημείωση για το δικαιολογητικό..."
              rows={4}
            />
          </div>
          <DialogFooter className="mt-4 flex justify-between">
            <Button 
              variant="destructive" 
              onClick={handleDeleteNote}
              disabled={!editingNoteDoc?.note}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Διαγραφή
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setNoteDialogOpen(false)}>
                Ακύρωση
              </Button>
              <Button onClick={handleSaveNote} className="bg-slate-900 hover:bg-slate-800">
                Αποθήκευση
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notification Settings Dialog */}
      <Dialog open={notificationDialogOpen} onOpenChange={setNotificationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Ρυθμίσεις Ειδοποιήσεων
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveNotificationSettings} className="space-y-6">
            <div className="space-y-4">
              {/* Info box about manual notifications */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">💡 Υπενθύμιση:</span> Οι υπενθυμίσεις προς τους συνεργάτες στέλνονται χειροκίνητα 
                  μέσω του <span className="font-semibold">Βοηθού Ειδοποιήσεων</span>.
                </p>
              </div>

              {/* Advisor Escalation Section - ONLY THIS REMAINS */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-slate-800">🚨 Ειδοποίηση Συμβούλου</h4>
                  <p className="text-sm text-slate-500">
                    Ειδοποίηση αν ο πελάτης δεν ανταποκρίνεται
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setNotificationSettings({
                    ...notificationSettings,
                    advisor_escalation_enabled: !notificationSettings.advisor_escalation_enabled
                  })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    notificationSettings.advisor_escalation_enabled ? 'bg-red-600' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      notificationSettings.advisor_escalation_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {notificationSettings.advisor_escalation_enabled && (
                <div className="space-y-2 bg-red-50 p-4 rounded-lg border border-red-200">
                  <Label htmlFor="impl-escalation-threshold">Αριθμός Αναπάντητων Υπενθυμίσεων</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      id="impl-escalation-threshold"
                      type="number"
                      min="1"
                      max="10"
                      value={notificationSettings.advisor_escalation_threshold}
                      onChange={(e) => setNotificationSettings({
                        ...notificationSettings,
                        advisor_escalation_threshold: parseInt(e.target.value) || 1
                      })}
                      className="w-24 bg-white"
                    />
                    <span className="text-slate-600">φορές</span>
                  </div>
                  <p className="text-xs text-slate-600 mt-2">
                    📞 Αν ο πελάτης δεν ανταποκριθεί σε <strong>{notificationSettings.advisor_escalation_threshold}</strong> υπενθυμίσ{notificationSettings.advisor_escalation_threshold === 1 ? 'η' : 'εις'} 
                    (μετά από <strong>{notificationSettings.advisor_escalation_threshold * 24} ώρες</strong>), θα λάβετε email να τον καλέσετε τηλεφωνικά.
                  </p>
                  {project.unanswered_reminders_count > 0 && (
                    <p className="text-xs text-red-600 font-medium mt-2">
                      ⚠️ Τρέχουσες αναπάντητες υπενθυμίσεις: {project.unanswered_reminders_count}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setNotificationDialogOpen(false)}
                className="flex-1"
              >
                Ακύρωση
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-slate-900 hover:bg-slate-800"
                disabled={loading}
              >
                {loading ? 'Αποθήκευση...' : 'Αποθήκευση'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Expiry Tracking Dialog */}
      <Dialog open={expiryDialogOpen} onOpenChange={setExpiryDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600" />
              Ημερομηνίες Ισχύος
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-md">
              <p className="text-sm font-medium">{expiryEditingDoc?.document_name}</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="impl-expiry-issue-date">Ημερομηνία Έκδοσης</Label>
              <Input
                id="impl-expiry-issue-date"
                type="date"
                value={expiryIssueDate}
                onChange={(e) => setExpiryIssueDate(e.target.value)}
                data-testid="impl-expiry-issue-date-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="impl-expiry-expiry-date">Ημερομηνία Λήξης</Label>
              <Input
                id="impl-expiry-expiry-date"
                type="date"
                value={expiryExpiryDate}
                onChange={(e) => setExpiryExpiryDate(e.target.value)}
                data-testid="impl-expiry-expiry-date-input"
              />
            </div>

            {expiryExpiryDate && (() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const expiry = new Date(expiryExpiryDate);
              expiry.setHours(0, 0, 0, 0);
              const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
              const expired = diffDays < 0;
              const expiringSoon = diffDays >= 0 && diffDays <= 30;
              return (
                <div className={`p-3 rounded-md text-sm ${
                  expired ? 'bg-red-50 text-red-700 border border-red-200' :
                  expiringSoon ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                  'bg-emerald-50 text-emerald-700 border border-emerald-200'
                }`}>
                  {expired ? (
                    <span>Το δικαιολογητικό έχει λήξει εδώ και <strong>{Math.abs(diffDays)}</strong> ημέρες</span>
                  ) : diffDays === 0 ? (
                    <span>Το δικαιολογητικό <strong>λήγει σήμερα</strong></span>
                  ) : (
                    <span>Απομένουν <strong>{diffDays}</strong> ημέρες μέχρι τη λήξη</span>
                  )}
                </div>
              );
            })()}
          </div>
          <DialogFooter className="mt-4 flex justify-between">
            <Button 
              variant="destructive" 
              onClick={handleClearExpiry}
              disabled={expirySaving || (!expiryEditingDoc?.expiry_date && !expiryEditingDoc?.issue_date)}
              size="sm"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Καθαρισμός
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setExpiryDialogOpen(false)}>
                Ακύρωση
              </Button>
              <Button 
                onClick={handleSaveExpiry} 
                className="bg-slate-900 hover:bg-slate-800"
                disabled={expirySaving}
                data-testid="impl-expiry-save-button"
              >
                {expirySaving ? 'Αποθήκευση...' : 'Αποθήκευση'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* R2 Relink Dialog */}
      <Dialog open={relinkDialogOpen} onOpenChange={setRelinkDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="r2-relink-dialog">
          <DialogHeader>
            <DialogTitle>
              {relinkStep === 'applied' ? 'Συγχρονισμός Ολοκληρώθηκε ✓' : 'Συγχρονισμός με Cloudflare R2'}
            </DialogTitle>
          </DialogHeader>
          
          {relinkLoading && !relinkResult ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-blue-600" />
              <p className="text-slate-600">Σαρώνω το R2 bucket...</p>
            </div>
          ) : relinkResult ? (
            <div className="space-y-4">
              {/* Diagnostic summary */}
              {relinkResult.diagnostic && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">📊 Κατάσταση Αρχείων Project:</h4>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="bg-slate-50 border rounded-lg p-2 text-center">
                      <div className="text-xl font-bold text-slate-700">{relinkResult.diagnostic.summary.total_files}</div>
                      <div className="text-xs text-slate-600">Σύνολο</div>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-center">
                      <div className="text-xl font-bold text-green-700">{relinkResult.diagnostic.summary.in_r2}</div>
                      <div className="text-xs text-slate-600">Στο R2</div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-2 text-center">
                      <div className="text-xl font-bold text-purple-700">{relinkResult.diagnostic.summary.in_gridfs}</div>
                      <div className="text-xs text-slate-600">Στο GridFS</div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-center">
                      <div className="text-xl font-bold text-red-700">{relinkResult.diagnostic.summary.lost_local}</div>
                      <div className="text-xs text-slate-600">Χαμένα</div>
                    </div>
                  </div>
                  {relinkResult.diagnostic.summary.r2_orphans_recoverable > 0 && (
                    <div className="mt-2 bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-800">
                      🔄 <strong>{relinkResult.diagnostic.summary.r2_orphans_recoverable}</strong> έγγραφα μπορούν να ανακτηθούν από το R2!
                    </div>
                  )}
                </div>
              )}

              {/* Relink summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-700">{relinkResult.projects_scanned}</div>
                  <div className="text-xs text-slate-600">Projects</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-700">{relinkResult.documents_relinked}</div>
                  <div className="text-xs text-slate-600">
                    {relinkStep === 'applied' ? 'Συνδέθηκαν' : 'Θα συνδεθούν'}
                  </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-slate-700">{relinkResult.documents_skipped}</div>
                  <div className="text-xs text-slate-600">Παραλήφθηκαν</div>
                </div>
              </div>

              {/* Status message */}
              {relinkResult.documents_relinked === 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800 font-medium mb-1">⚠️ Δεν βρέθηκαν αρχεία προς ανάκτηση</p>
                  <p className="text-xs text-amber-700">
                    Είτε όλα τα αρχεία είναι ήδη σωστά συνδεδεμένα, είτε δεν υπάρχουν αντίγραφα στο R2 για αυτό το project.
                    Σε αυτή την περίπτωση, τα αρχεία πρέπει να ανέβουν ξανά.
                  </p>
                </div>
              ) : relinkStep === 'applied' ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800 font-medium">
                    ✓ Επανήλθαν {relinkResult.documents_relinked} έγγραφα. Δοκιμάστε ξανά το κατέβασμα τώρα.
                  </p>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    Βρέθηκαν <strong>{relinkResult.documents_relinked}</strong> έγγραφα που μπορούν να συνδεθούν ξανά με τα αρχεία τους στο R2.
                    Πατήστε <strong>"Εφαρμογή"</strong> για να επανέλθουν στη βάση.
                  </p>
                </div>
              )}

              {/* Details list */}
              {relinkResult.details && relinkResult.details.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">
                    Λεπτομέρειες ({relinkResult.details.length}):
                  </h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-2 bg-slate-50">
                    {relinkResult.details.map((d, idx) => (
                      <div key={idx} className="text-xs bg-white border rounded p-2">
                        <div className="font-medium text-slate-900">{d.document_name || d.document_id}</div>
                        <div className="text-slate-500">
                          {d.collection === 'implementation_general_documents' ? 'Γενικά' : 'Παραστατικά'} •{' '}
                          {d.new_paths.length} αρχείο(α) στο R2
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <div className="flex justify-end gap-2 pt-4 border-t mt-4">
            {relinkStep === 'preview' && relinkResult?.documents_relinked > 0 && (
              <Button
                onClick={handleR2RelinkApply}
                disabled={relinkLoading}
                className="bg-green-600 hover:bg-green-700"
                data-testid="r2-relink-apply-btn"
              >
                {relinkLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Εφαρμογή
              </Button>
            )}
            <Button variant="outline" onClick={() => setRelinkDialogOpen(false)} data-testid="r2-relink-close-btn">
              {relinkStep === 'applied' ? 'Κλείσιμο' : 'Άκυρο'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImplementationProjectDetail;
